
import { useState, useCallback, useMemo } from 'react';
import { useCards } from '@/contexts/cards';
import { useAccounts } from '@/contexts/accounts';

export const useCardFormValidation = (initialData?: any) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [nameError, setNameError] = useState('');
  const { cards } = useCards();
  const { accounts } = useAccounts();

  // Get existing card names for validation (excluding current card if editing)
  const existingCardNames = useMemo(() => {
    return cards
      .filter(card => initialData ? card.id !== initialData.id : true)
      .map(card => card.name.toLowerCase().trim());
  }, [cards, initialData?.id]);

  // Native validation handles empty fields, we only check business logic
  const validateName = useCallback((name: string, hasInteracted: boolean = false) => {
    if (!name.trim()) {
      return ''; // Let native validation handle empty fields
    }
    
    if (name.trim().length < 2) {
      return 'Nome deve ter pelo menos 2 caracteres';
    }
    
    if (existingCardNames.includes(name.toLowerCase().trim())) {
      return 'Já existe um cartão com este nome';
    }
    
    return '';
  }, [existingCardNames]);

  const validateBank = useCallback((bank: string, hasInteracted: boolean = false) => {
    return ''; // Native validation handles empty fields
  }, []);

  const validateLimit = useCallback((limit: string, hasInteracted: boolean = false) => {
    if (!limit || limit === '' || limit === '0' || limit === '0,00') {
      if (hasInteracted) {
        return 'Limite é obrigatório e deve ser maior que zero';
      }
      return '';
    }
    
    const numericLimit = parseFloat(limit.replace(/\./g, '').replace(',', '.'));
    if (isNaN(numericLimit) || numericLimit <= 0) {
      return 'Limite deve ser um valor válido maior que zero';
    }
    
    return '';
  }, []);

  const validateClosingDate = useCallback((closingDate: string, hasInteracted: boolean = false) => {
    if (!closingDate) {
      return ''; // Native validation handles empty fields
    }
    
    const day = parseInt(closingDate);
    if (isNaN(day) || day < 1 || day > 31) {
      return 'Data deve estar entre 1 e 31';
    }
    
    return '';
  }, []);

  const validateDueDate = useCallback((dueDate: string, hasInteracted: boolean = false) => {
    if (!dueDate) {
      return ''; // Native validation handles empty fields
    }
    
    const day = parseInt(dueDate);
    if (isNaN(day) || day < 1 || day > 31) {
      return 'Data deve estar entre 1 e 31';
    }
    
    return '';
  }, []);

  const validateAccountId = useCallback((accountId: string, hasInteracted: boolean = false) => {
    if (!accountId) {
      return ''; // Native validation handles empty fields
    }
    
    const accountExists = accounts.some(account => account.id === accountId);
    if (!accountExists) {
      return 'Conta selecionada não existe';
    }
    
    return '';
  }, [accounts]);

  const validateForm = useCallback((formData: any) => {
    const newErrors: Record<string, string> = {};
    
    // Validate all fields with interaction = true to show all errors
    const nameErr = validateName(formData.name, true);
    const bankErr = validateBank(formData.bank, true);
    const limitErr = validateLimit(formData.limit, true);
    const closingDateErr = validateClosingDate(formData.closingDate, true);
    const dueDateErr = validateDueDate(formData.dueDate, true);
    const accountIdErr = validateAccountId(formData.accountId, true);
    
    // Additional check: ensure limit is > 0 on final validation
    const numericLimit = parseFloat((formData.limit || '0').replace(/\./g, '').replace(',', '.'));
    if (!formData.limit || numericLimit <= 0) {
      newErrors.limit = 'Limite é obrigatório e deve ser maior que zero';
    } else if (limitErr) {
      newErrors.limit = limitErr;
    }
    
    // Ensure account is selected
    if (!formData.accountId || formData.accountId.trim() === '') {
      newErrors.accountId = 'Conta vinculada é obrigatória';
    } else if (accountIdErr) {
      newErrors.accountId = accountIdErr;
    }
    
    if (nameErr) newErrors.name = nameErr;
    if (bankErr) newErrors.bank = bankErr;
    if (closingDateErr) newErrors.closingDate = closingDateErr;
    if (dueDateErr) newErrors.dueDate = dueDateErr;
    
    setErrors(newErrors);
    setNameError(nameErr);
    
    return Object.keys(newErrors).length === 0;
  }, [validateName, validateBank, validateLimit, validateClosingDate, validateDueDate, validateAccountId]);

  const handleNameChange = useCallback((value: string, setFormData: (updater: (prev: any) => any) => void) => {
    setFormData(prev => ({ ...prev, name: value }));
    const error = validateName(value, true);
    setNameError(error);
    
    if (error) {
      setErrors(prev => ({ ...prev, name: error }));
    } else {
      setErrors(prev => {
        const { name, ...rest } = prev;
        return rest;
      });
    }
  }, [validateName]);

  const getNameErrorType = useCallback((formData: any, hasInteracted: boolean = false): 'default' | 'warning' | 'popup' => {
    const error = validateName(formData.name, hasInteracted);
    if (error.includes('obrigatório')) return 'default';
    if (error.includes('2 caracteres')) return 'warning';
    if (error.includes('já existe')) return 'popup';
    return 'default';
  }, [validateName]);

  const getNameErrorMessage = useCallback((formData: any, hasInteracted: boolean = false) => {
    return validateName(formData.name, hasInteracted);
  }, [validateName]);

  const resetValidation = useCallback(() => {
    setErrors({});
    setNameError('');
  }, []);

  return {
    errors,
    nameError,
    validateForm,
    handleNameChange,
    getNameErrorType,
    getNameErrorMessage,
    resetValidation
  };
};
